import { useInlineRename } from 'hooks/useInlineRename';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import * as S from './styles';
import type { InlineRenameTrigger } from 'hooks/useInlineRename';
import type { InlineRenameVariant } from './styles';

export interface InlineRenamableTitleRef {
	startEditing: () => void;
	commitEditing: () => void;
}

interface InlineRenamableTitleProps {
	value: string;
	onCommit: (trimmed: string) => void;
	editable?: boolean;
	variant?: InlineRenameVariant;
	trigger?: InlineRenameTrigger;
	useOverlay?: boolean;
	onBeforeStart?: () => boolean;
	// When true, enters edit mode on mount (e.g. FontList pencil action).
	autoStartEditing?: boolean;
	onEditingChange?: (isEditing: boolean) => void;
	className?: string;
	title?: string;
	'data-testid'?: string;
}

const InlineRenamableTitle = forwardRef<InlineRenamableTitleRef, InlineRenamableTitleProps>(
	(
		{
			value,
			onCommit,
			editable = true,
			variant = 'navbar',
			trigger = 'click',
			useOverlay = false,
			onBeforeStart,
			autoStartEditing = false,
			onEditingChange,
			className,
			title,
			'data-testid': dataTestId,
		},
		ref
	) => {
		const rename = useInlineRename({
			value,
			editable,
			onCommit,
			onBeforeStart,
			trigger,
		});

		useImperativeHandle(ref, () => ({
			startEditing: rename.startEditing,
			commitEditing: rename.commitEditing,
		}));

		useEffect(() => {
			onEditingChange?.(rename.isEditing);
		}, [rename.isEditing, onEditingChange]);

		// Only auto-start when the prop turns on — never cancel manual click-to-edit.
		useEffect(() => {
			if (autoStartEditing && !rename.isEditing) {
				rename.startEditing();
			}
		}, [autoStartEditing, rename.isEditing, rename.startEditing]);

		const displayText = value || '\u00A0';

		// Keyboard activation when not editing: Tab to focus, then Enter/F2 to
		// start renaming \u2014 matches OS-level rename conventions (F2 in Windows
		// Explorer, Enter in Finder). Skipped during edit (the input has its
		// own keyboard semantics) and when disabled.
		const wrapperKeyboardProps =
			!rename.isEditing && editable
				? {
						tabIndex: 0,
						role: 'button' as const,
						onKeyDown: (e: React.KeyboardEvent) => {
							if (e.key === 'Enter' || e.key === 'F2') {
								e.preventDefault();
								rename.startEditing();
							}
						},
				  }
				: {};

		return (
			<>
				{useOverlay && rename.isEditing && (
					<S.RenameOverlay onMouseDown={rename.commitEditing} />
				)}
				<S.Wrapper
					$variant={variant}
					$raised={useOverlay && rename.isEditing}
					className={className}
					{...rename.wrapperProps}
					{...wrapperKeyboardProps}
					title={!rename.isEditing ? title ?? value : undefined}
					data-testid={dataTestId}
				>
					{rename.isEditing ? (
						<S.EditInput $variant={variant} {...rename.inputProps} />
					) : (
						<S.Display $variant={variant}>{displayText}</S.Display>
					)}
				</S.Wrapper>
			</>
		);
	}
);

InlineRenamableTitle.displayName = 'InlineRenamableTitle';

export default InlineRenamableTitle;
